/* eslint-disable no-self-compare */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Alert,
  AlertTitle,
  Chip,
  Skeleton,
  Stack,
  LinearProgress,
} from "@mui/material";
import theme from "lib/theme";
import { Asset } from "types/asset";
import {
  DataGridPro,
  GridColDef,
  GridHeaderSelectionCheckboxParams,
  GridRenderCellParams,
  GridRowId,
  GridSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import AssetsExportGridItem from "./AssetsExportGridItem";
import SelectAllAssetsBanner from "./SelectAllAssetsBanner";
import { formatDate } from "utils/helpers";
import { PickupStatus } from "pages/Pickups/PickupStatus";
import { StatusChip } from 'components/common/StatusChip';
import { useTerminology } from 'hooks/useTerminology';
import { getOrganizationType } from 'lib/organizationType';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

type AssetGridProps = {
  replace: (field: string, value: string) => void;
  assets: Asset[];
  isLoading: boolean;
  isError: boolean;
  meta: {
    count: number;
    totalPages: number;
  };
  refetch: () => void;
  selectedAssetIds: GridSelectionModel;
  setSelectedAssetIds: (x: GridSelectionModel) => void;
  setSelectAllFlag?: (x: boolean) => void;
  selectAllFlag?: boolean;
  assetsTab: string;
  setSecurityLocksOpen: (x: boolean) => void;
  setRetiredAssetOpen: (x: boolean) => void;
  getAllSerialsNumbers: (
    asset_ids?: GridRowId[],
    selectAllFlag?: boolean
  ) => Promise<void>;
  fieldsToHide: string[];
};

const CustomToolbar = ({ selectAllFlag }: { selectAllFlag?: boolean }) => {
  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssetsExportGridItem selectAllFlag={selectAllFlag} />
      </Box>
    </GridToolbarContainer>
  );
};

export function AssetsGrid({
  replace,
  assets,
  setSelectedAssetIds,
  selectedAssetIds,
  isLoading,
  isError,
  meta,
  refetch,
  setSelectAllFlag,
  selectAllFlag,
  assetsTab,
  setSecurityLocksOpen,
  setRetiredAssetOpen,
  getAllSerialsNumbers,
  fieldsToHide,
}: AssetGridProps) {
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [headerSelectAll, setHeaderSelectAll] = useState(false);
  const terminology = useTerminology();
  const organizationType = getOrganizationType();
  const isAssettech = organizationType === 'assettech';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const apiRef = useGridApiRef();

  const committedSearch = searchParams.get('search') || '';

  useEffect(() => {
    setPageNumber(0);
  }, [committedSearch]);

  const handleSelectAllChange = useCallback(
    (selectedAll: boolean) => {
      if (selectedAll) {
        // When selecting all via header checkbox, set selectAllFlag to true
        // This will select ALL assets across all pages, not just current page
        setSelectAllFlag?.(true);
        setHeaderSelectAll(true);
        // Don't set selectedAssetIds here - let the banner handle it
      } else {
        // When deselecting, clear everything
        setSelectAllFlag?.(false);
        setHeaderSelectAll(false);
        setSelectedAssetIds([]);
      }
    },
    [setSelectAllFlag, setSelectedAssetIds]
  );

  useEffect(() => {
    return apiRef.current.subscribeEvent(
      'headerSelectionCheckboxChange',
      (a: GridHeaderSelectionCheckboxParams, event) => {
        event.defaultMuiPrevented = true;
        handleSelectAllChange(a.value);
      }
    );
  }, [apiRef, handleSelectAllChange]);

  useEffect(() => {
    // Reset header select all when assets change (due to pagination/filtering)
    setHeaderSelectAll(false);
  }, [assets]);

  useEffect(() => {
    replace('page[number]', (pageNumber + 1).toString());
  }, [pageNumber, replace]);

  useEffect(() => {
    replace('page[size]', pageSize.toString());
  }, [pageSize, replace]);

  // Asset status configurations for the StatusChip component
  const getAssetStatusConfig = (status: string) => {
    const statusConfigs: Record<
      string,
      { backgroundColor: string; color: string }
    > = {
      Available: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
      },
      Enrolled: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
      },
      'Checked Out': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.dark,
      },
      'Pending Pickup': {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
      },
      'Verifying Release': {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
      },
      Retired: { backgroundColor: '#bcc2be', color: '#424242' },
      Repurposed: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
      },
      Recycled: { backgroundColor: '#bcc2be', color: '#424242' },
    };

    return (
      statusConfigs[status] || {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
      }
    );
  };

  // Default columns for non-assettech customers
  const defaultColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'STATUS',
        width: 175,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const asset = params.row;

          return (
            <Box display='flex' alignItems='center' gap={1}>
              <StatusChip
                status={asset.status || 'Unknown'}
                size='small'
                customStyles={getAssetStatusConfig(asset.status)}
              />
            </Box>
          );
        },
      },
      {
        field: 'orderNumber',
        headerName: terminology.Pickups.toUpperCase(),
        width: 125,
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.pickup?.orderNumber}
            </Box>
          );
        },
      },
      {
        field: 'scheduledDate',
        headerName: 'SCHEDULED DATE',
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {formatDate(params.row.pickup?.scheduledDate)}
            </Box>
          );
        },
      },
      {
        field: 'state',
        headerName: `${terminology.Pickup.toUpperCase()} STATUS`,
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.pickup && <PickupStatus pickup={params.row.pickup} />}
            </Box>
          );
        },
      },
      {
        field: 'address',
        headerName: 'ADDRESS',
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.location?.address}
            </Box>
          );
        },
      },
      {
        field: 'manufacturer',
        headerName: 'MANUFACTURER',
        sortable: false,
        width: 175,
      },
      {
        field: 'deviceType',
        headerName: 'DEVICE TYPE',
        sortable: false,
        width: 175,
      },
      {
        field: 'serialNumber',
        headerName: 'SERIAL',
        sortable: false,
        width: 175,
      },
      {
        field: 'model',
        headerName: 'MODEL',
        sortable: false,
        width: 175,
      },
      {
        field: 'assetTag',
        headerName: 'ASSET TAG',
        sortable: false,
        width: 175,
      },
      {
        field: 'quantity',
        headerName: 'QUANTITY',
        sortable: false,
        width: 100,
      },
    ],
    [terminology]
  );

  // Assettech-specific columns with the exact mapping you requested
  const assettechColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'lifecycle',
        headerName: 'REGISTRY',
        width: 118,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const st = String(params.row.state || "").toLowerCase();
          if (st === "active" || st === "unlocked") {
            return (
              <Chip
                label="Live"
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: theme.palette.success.light,
                  color: theme.palette.success.dark,
                }}
              />
            );
          }
          if (st === "retired") {
            return (
              <Chip
                label="Retired"
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#eceff1",
                  color: "#455a64",
                }}
              />
            );
          }
          return (
            <Chip
              label={params.row.state || "—"}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "locationSummary",
        headerName: "LOCATION",
        width: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const loc = params.row.location;
          const text = loc?.name || loc?.address || "—";
          return (
            <Box
              title={text}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {text}
            </Box>
          );
        },
      },
      {
        field: "employeeSummary",
        headerName: "EMPLOYEE",
        width: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const e = params.row.employee;
          const name =
            [e?.firstName, e?.lastName].filter(Boolean).join(" ") ||
            e?.email ||
            "—";
          return (
            <Box
              title={name}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {name}
            </Box>
          );
        },
      },
      {
        field: 'unitNumber',
        headerName: 'UNIT ID',
        width: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.unitNumber || ''}
            </Box>
          );
        },
      },
      {
        field: 'assetTag',
        headerName: 'CUST. ASSET TAG',
        sortable: false,
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.assetTag || ''}
            </Box>
          );
        },
      },
      {
        field: 'orderNumber',
        headerName: 'JOB NO',
        width: 120,
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.pickup?.orderNumber || ''}
            </Box>
          );
        },
      },
      {
        field: 'deviceType',
        headerName: 'PRODUCT TYPE',
        sortable: false,
        width: 140,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.deviceType || ''}
            </Box>
          );
        },
      },
      {
        field: 'manufacturer',
        headerName: 'MAKE',
        sortable: false,
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.manufacturer || ''}
            </Box>
          );
        },
      },
      {
        field: 'model',
        headerName: 'MODEL',
        sortable: false,
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.model || ''}
            </Box>
          );
        },
      },
      {
        field: 'serialNumber',
        headerName: 'SERIAL NO.',
        sortable: false,
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.serialNumber || ''}
            </Box>
          );
        },
      },
      {
        field: 'cpu',
        headerName: 'CPU',
        sortable: false,
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.cpu || ''}
            </Box>
          );
        },
      },
      {
        field: 'ram',
        headerName: 'RAM',
        sortable: false,
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.ram || ''}
            </Box>
          );
        },
      },
      {
        field: 'hardDrive',
        headerName: 'STORAGE',
        sortable: false,
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.hardDrive || ''}
            </Box>
          );
        },
      },
      {
        field: 'condition',
        headerName: 'GRADE',
        sortable: false,
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.condition || ''}
            </Box>
          );
        },
      },
      {
        field: 'weight',
        headerName: 'PRODUCT WEIGHT (KG)',
        sortable: false,
        width: 180,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.weight || ''}
            </Box>
          );
        },
      },
      {
        field: 'wipeStatus',
        headerName: 'DD STATUS',
        sortable: false,
        width: 160,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.ddStatus ?? params.row.wipeStatus ?? ''}
            </Box>
          );
        },
      },
      {
        field: 'state',
        headerName: 'JOB STATUS',
        width: 150,
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.pickup?.state || ''}
            </Box>
          );
        },
      },
      {
        field: 'status',
        headerName: 'EOL STATUS',
        width: 170,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display='flex' alignItems='center' gap={1}>
              {params.row.eolStatus ?? params.row.status ?? ''}
            </Box>
          );
        },
      },
    ],
    []
  );

  // Choose the appropriate columns based on organization
  const columns: GridColDef[] = useMemo(() => {
    return isAssettech ? assettechColumns : defaultColumns;
  }, [isAssettech, assettechColumns, defaultColumns]);

  if (isError) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: theme.palette.divider,
          background: 'white',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Alert severity='error'>
          <AlertTitle>Error</AlertTitle>
          Something went wrong
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '80vh' }}>
      {headerSelectAll ? (
        <SelectAllAssetsBanner
          selectedAssetIds={selectedAssetIds}
          selectAllFlag={selectAllFlag}
          meta={meta}
          setSelectAllFlag={
            setSelectAllFlag
              ? (value: boolean | ((prevState: boolean) => boolean)) => {
                  if (typeof value === 'function') {
                    setSelectAllFlag(value(selectAllFlag || false));
                  } else {
                    setSelectAllFlag(value);
                  }
                }
              : undefined
          }
          setSelectedAssetIds={(
            value:
              | GridSelectionModel
              | ((prevState: GridSelectionModel) => GridSelectionModel)
          ) => {
            if (typeof value === 'function') {
              setSelectedAssetIds(value(selectedAssetIds));
            } else {
              setSelectedAssetIds(value);
            }
          }}
          setHeaderSelectAll={(
            value: boolean | ((prevState: boolean) => boolean)
          ) => {
            if (typeof value === 'function') {
              setHeaderSelectAll(value(headerSelectAll));
            } else {
              setHeaderSelectAll(value);
            }
          }}
        />
      ) : null}
      <DataGridPro
        sx={{ height: '80vh', cursor: isAssettech ? 'pointer' : undefined }}
        apiRef={apiRef}
        paginationMode='server'
        pagination
        rows={assets}
        columns={columns}
        checkboxSelection
        onRowClick={
          isAssettech
            ? (params) => {
                if (params.row.uuid) {
                  navigate(`/assets/${params.row.uuid}/edit`, {
                    state: {
                      from: `${location.pathname}${location.search}`,
                    },
                  });
                }
              }
            : undefined
        }
        selectionModel={selectedAssetIds}
        onSelectionModelChange={setSelectedAssetIds}
        rowsPerPageOptions={[25, 50, 100]}
        page={pageNumber}
        rowCount={meta.count}
        initialState={{
          columns: {
            columnVisibilityModel: {
              orderNumber: !fieldsToHide.includes('orderNumber'),
              scheduledDate: !fieldsToHide.includes('scheduledDate'),
              address: !fieldsToHide.includes('address'),
              state: !fieldsToHide.includes('state'),
              endOfLifeCondition: !fieldsToHide.includes('endOfLifeCondition'),
              quantity: !fieldsToHide.includes('quantity'),
              assetTag: !fieldsToHide.includes('assetTag'),
            },
          },
          pinnedColumns: {
            left: isAssettech ? ['lifecycle'] : [],
            right: ['actions'],
          },
        }}
        onPageChange={(page) => {
          setPageNumber(page);
        }}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
        }}
        pageSize={pageSize}
        components={{
          Toolbar: () => <CustomToolbar selectAllFlag={selectAllFlag} />,
          NoResultsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              No assets matching your filters
            </Stack>
          ),
          LoadingOverlay: () => <LinearProgress />,
        }}
      />
      {isLoading && (
        <Box paddingTop={5}>
          <Skeleton variant='text' height={75} />
          <Skeleton variant='text' height={75} />
          <Skeleton variant='text' height={75} />
        </Box>
      )}

      {/* NotFoundAssetModal is handled elsewhere in the assets page */}
    </Box>
  );
}
